import {
  BankAccountIcon,
  BasketShoppingSolidIcon,
  Box,
  CalendarDaySolidIcon,
  Disclaimer,
  Div,
  formatAmount,
  H3,
  H5,
  lightTheme,
  ListSpinner,
  ModalPortal,
  NumberType,
  P,
  Separator,
  VerticalSeparator,
} from '@vgrubs/components';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { saasRange } from './saasDetails.helpers';
import { SaasPaymentsSection } from './SaasPaymentsSection';
import {
  SaasLocationCharges,
  SaasLocationChargesSection,
} from './SaasLocationChargesSection';
import { useSaasDetails } from './useSaasDetails';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  /** The overview's range (yyyy-MM-dd), so the totals match its row. */
  from: string;
  to: string;
  periodLabel?: string;
};

type Source = 'perMonth' | 'perOrder' | 'perDay';

const usd = (cents: number) => formatAmount(cents, NumberType.USD);

/** The 40px grey glyph the Transactions summary cards use; these icons ship
 *  smaller, so the size is set here rather than on the shared SVGs. */
const CardIcon = styled.div`
  display: flex;
  flex: none;

  svg {
    width: 40px;
    height: 40px;
  }
`;

type SourceCardProps = {
  active: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  amount: number;
  note: string;
  width: string;
};

/**
 * One source of the SaaS income, and the switch for the list under it: the
 * Transactions summary card, white when picked and outlined when not.
 */
const SourceCard = ({
  active,
  onSelect,
  icon,
  title,
  amount,
  note,
  width,
}: SourceCardProps) => (
  <Box
    $flex
    $alignCenter
    $pointer
    $width={width}
    $widthSM="100%"
    $box={active}
    $outlineBox={!active}
    onClick={onSelect}
  >
    <CardIcon>{icon}</CardIcon>
    <VerticalSeparator
      $m="0 20px"
      $mSM="0 16px"
      $h="40px"
      $color={lightTheme.lightGray}
    />
    <Div>
      <H5 $m="0">{title}</H5>
      <H3 $m="0">{usd(amount)}</H3>
      <P $m="0">{note}</P>
    </Div>
  </Box>
);

/**
 * What "Monthly Saas" on the Finances overview is made of: monthly GoCardless
 * payments and the vTablet fee charged per order or per day. Opened from the arrow on
 * that row of the Gross and Net Revenue cards, for the same range.
 */
export const SaasDetailsModal = ({
  isOpen,
  closeModal,
  from,
  to,
  periodLabel,
}: Props) => {
  const range = saasRange(from, to);
  const [source, setSource] = useState<Source>('perMonth');
  const { details, isLoading, isError } = useSaasDetails(range, isOpen);
  // An older backend sends no `perDay` and counts the daily charges in
  // `perOrder`, so the card only shows once it is split out.
  const perDay = details?.perDay;
  const cardWidth = perDay ? '33.33%' : '50%';
  const perOrderLocations: SaasLocationCharges[] =
    details?.perOrder.locations.map((location) => ({
      ...location,
      fee: location.perOrderFee,
      count: location.orders,
    })) ?? [];
  const perDayLocations: SaasLocationCharges[] =
    perDay?.locations.map((location) => ({
      ...location,
      fee: location.perDayFee,
      count: location.charges,
    })) ?? [];

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={closeModal}
      backgroundColor={lightTheme.white}
      padding="24px"
      logo={false}
      width="1200px"
      maxHeight="80vh"
      modalOverflow="auto"
      titleWithExit="Monthly Saas"
      subtitle={periodLabel}
      // Above the cards' "View all" modal, which can list this row too.
      zIndex={135}
    >
      {isLoading && (
        <ListSpinner loading height="160px" heightSM="160px" mb="0" mbSM="0" />
      )}

      {isError && (
        <P $m="24px 0" $center $color={lightTheme.primary}>
          The SaaS details could not be loaded.
        </P>
      )}

      {details && (
        <>
          <Div $flex $gap="40px" $mb="4">
            <div>
              <P $m="0" $color={lightTheme.lightGray}>
                Gross
              </P>
              <H3 $m="0" $color={lightTheme.green}>
                {usd(details.gross)}
              </H3>
            </div>
            <div>
              <P $m="0" $color={lightTheme.lightGray}>
                Net
              </P>
              <H3 $m="0" $color={lightTheme.green}>
                {usd(details.net)}
              </H3>
            </div>
          </Div>
          <Disclaimer $m="0 0 16px" $color={lightTheme.lightGray}>
            Net leaves out the GoCardless fee on the monthly payments. Per-order
            and per-day charges have no GoCardless cost.
          </Disclaimer>

          <Div $flex $columnSM $gap="20px" $gapSM="16px" $mb="24">
            <SourceCard
              active={source === 'perMonth'}
              onSelect={() => setSource('perMonth')}
              icon={<BankAccountIcon color={lightTheme.lightGray} />}
              title="Per month"
              amount={details.perMonth.gross}
              width={cardWidth}
              note={`Net ${usd(details.perMonth.net)} · GoCardless fee ${usd(
                details.perMonth.gocardlessFee
              )}`}
            />
            <SourceCard
              active={source === 'perOrder'}
              onSelect={() => setSource('perOrder')}
              icon={<BasketShoppingSolidIcon color={lightTheme.lightGray} />}
              title="Per order"
              amount={details.perOrder.amount}
              note={`${details.perOrder.orders} orders · no GoCardless fee`}
              width={cardWidth}
            />
            {perDay && (
              <SourceCard
                active={source === 'perDay'}
                onSelect={() => setSource('perDay')}
                icon={<CalendarDaySolidIcon color={lightTheme.lightGray} />}
                title="Per day"
                amount={perDay.amount}
                note={`${perDay.charges} ${
                  perDay.charges === 1 ? 'charge' : 'charges'
                } · no GoCardless fee`}
                width={cardWidth}
              />
            )}
          </Div>

          <Separator $m="0 0 16px" />
          {source === 'perMonth' && (
            <>
              <H5 $mb="12">Monthly payments</H5>
              <SaasPaymentsSection
                range={range}
                statuses={details.perMonth.statuses}
              />
            </>
          )}
          {source === 'perOrder' && (
            <>
              <H5 $mb="12">Charged per order, by location</H5>
              <SaasLocationChargesSection
                unit="order"
                locations={perOrderLocations}
              />
            </>
          )}
          {source === 'perDay' && (
            <>
              <H5 $mb="12">Charged per day, by location</H5>
              <SaasLocationChargesSection
                unit="day"
                locations={perDayLocations}
              />
            </>
          )}
        </>
      )}
    </ModalPortal>
  );
};
